/**
 * How a batch is summarised, shared by the reducer and the selectors.
 *
 * It lives apart from both because they both need it: the reducer asks whether a
 * batch is still running when Clear drops the finished ones, and the selectors
 * ask the same question for the UI. Keeping it in selectors.ts meant the reducer
 * importing from the layer above it.
 */
import { DownloadBatchState, DownloadFileState } from './models';

export interface DownloadSummary {
  /** Files in the batch. */
  readonly total: number;
  readonly done: number;
  readonly errored: number;
  /**
   * Files moving right now — three at a time, so the progress line can say how
   * many the file it names stands for, rather than letting one name quietly
   * speak for all of them.
   */
  readonly activeCount: number;
  /** True while at least one file is still queued or downloading. */
  readonly isActive: boolean;
  /** Bytes written across the batch. Monotonic. */
  readonly loadedBytes: number;
  /**
   * Bytes expected across the batch, or `undefined` when any single file's size
   * is unknown. All-or-nothing on purpose — the same rule Download Review uses
   * for its File Size column (see downloadReviewRows.ts). A denominator that
   * summed only the known files would read as the batch total and would grow as
   * more sizes arrived, which is worse than showing none.
   */
  readonly totalBytes?: number;
}

export const summarizeFiles = (
  files: ReadonlyArray<DownloadFileState>
): DownloadSummary => {
  let done = 0;
  let errored = 0;
  let queued = 0;
  let activeCount = 0;
  let loadedBytes = 0;
  let totalBytes: number | undefined = 0;

  // One pass. This runs for every batch on every progress action — four times a
  // second per file in flight — so the counts are tallied here rather than by a
  // `filter` or `some` per field over the same array.
  files.forEach((file) => {
    if (file.status === 'done') {
      done += 1;
    }
    if (file.status === 'error') {
      errored += 1;
    }
    if (file.status === 'queued') {
      queued += 1;
    }
    if (file.status === 'downloading') {
      activeCount += 1;
    }
    loadedBytes += file.loaded;
    if (totalBytes !== undefined) {
      totalBytes =
        typeof file.total === 'number' ? totalBytes + file.total : undefined;
    }
  });

  return {
    total: files.length,
    done,
    activeCount,
    errored,
    isActive: queued > 0 || activeCount > 0,
    loadedBytes,
    totalBytes,
  };
};

/** Whether anything in this batch is still queued or moving. */
export const isBatchRunning = (batch: DownloadBatchState): boolean =>
  summarizeFiles(batch.files).isActive;
